import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Add, ChatBubbleOutline, GroupAdd, Send } from "@material-ui/icons";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemText, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatMontevideoTime } from "../../utils/dateTime";

const useStyles = makeStyles((theme) => ({
  chatShell: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    margin: theme.spacing(1.5),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    background: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: { margin: theme.spacing(1) },
  },
  channelsPanel: {
    display: "flex",
    flexDirection: "column",
    width: 250,
    flex: "0 0 250px",
    minWidth: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    [theme.breakpoints.down(900)]: { width: 142, flexBasis: 142 },
    [theme.breakpoints.down(420)]: { width: 112, flexBasis: 112 },
  },
  channelsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 58,
    padding: theme.spacing(0, 1, 0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  channelsTitle: { fontWeight: 700 },
  channelsList: { flex: 1, overflowY: "auto" },
  channelItem: {
    minWidth: 0,
    borderLeft: "3px solid transparent",
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
      borderLeftColor: theme.palette.primary.main,
    },
  },
  channelText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  conversation: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    flexDirection: "column",
    background: theme.palette.background.default,
  },
  conversationHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 58,
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  headerCopy: { flex: 1, minWidth: 0 },
  headerIcon: { color: theme.palette.primary.main },
  headerText: { fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  headerMembers: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  messages: { flex: 1, overflowY: "auto", padding: theme.spacing(2), minWidth: 0 },
  messageRow: { display: "flex", marginBottom: theme.spacing(1.25) },
  ownMessage: { justifyContent: "flex-end" },
  messageBubble: {
    maxWidth: "78%",
    padding: theme.spacing(1, 1.25),
    borderRadius: 10,
    overflowWrap: "anywhere",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
  },
  ownBubble: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "& .MuiTypography-colorTextSecondary": { color: alpha(theme.palette.primary.contrastText, 0.78) },
  },
  messageMeta: { display: "block", marginBottom: 3 },
  empty: { maxWidth: 260, margin: `${theme.spacing(7)}px auto`, textAlign: "center" },
  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  composerInput: { flex: 1, minWidth: 0 },
  sendButton: { flex: "0 0 auto" },
}));

const InternalChat = () => {
  const classes = useStyles();
  const { isAuth, user } = useContext(AuthContext);
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [membersOpen, setMembersOpen] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [membersSaving, setMembersSaving] = useState(false);
  const messagesRef = useRef(null);
  const loadedChannelRef = useRef(null);
  const stayAtBottomRef = useRef(true);
  const forceScrollRef = useRef(true);

  const loadChat = useCallback(async (selectedChannelId) => {
    try {
      const { data } = await api.get("/crm/internal-chat", { params: selectedChannelId ? { channelId: selectedChannelId } : {} });
      const nextChannelId = data.channelId || null;
      if (String(loadedChannelRef.current) !== String(nextChannelId)) {
        loadedChannelRef.current = nextChannelId;
        stayAtBottomRef.current = true;
        forceScrollRef.current = true;
      }
      setChannels(data.channels || []);
      setChannelId(nextChannelId);
      setMessages((current) => {
        const incoming = data.messages || [];
        const unchanged = current.length === incoming.length && current.every((message, index) =>
          String(message.id) === String(incoming[index]?.id)
          && message.body === incoming[index]?.body
          && message.updatedAt === incoming[index]?.updatedAt,
        );
        return unchanged ? current : incoming;
      });
    } catch (error) { toastError(error); }
  }, []);

  useEffect(() => { if (isAuth) loadChat(); }, [isAuth, loadChat]);
  useEffect(() => {
    if (!isAuth || !channelId) return undefined;
    const intervalId = setInterval(() => loadChat(channelId), 1200);
    return () => clearInterval(intervalId);
  }, [isAuth, channelId, loadChat]);
  useEffect(() => {
    if (!isAuth) return;
    api.get("/crm/task-assignees")
      .then(({ data }) => setUsers(data.users || []))
      .catch(toastError);
  }, [isAuth]);
  useEffect(() => {
    if (!forceScrollRef.current && !stayAtBottomRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      forceScrollRef.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, channelId]);

  const createChannel = async () => {
    const name = window.prompt("Nombre del canal interno");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/crm/internal-chat/channels", { name: name.trim() });
      await loadChat(data.id);
    } catch (error) { toastError(error); }
  };

  const sendMessage = async (event) => {
    event?.preventDefault();
    if (!body.trim() || !channelId || sending) return;
    setSending(true);
    try {
      stayAtBottomRef.current = true;
      forceScrollRef.current = true;
      await api.post("/crm/internal-chat/messages", { channelId, body: body.trim() });
      setBody("");
      await loadChat(channelId);
    } catch (error) { toastError(error); }
    finally { setSending(false); }
  };

  const activeChannel = channels.find((channel) => Number(channel.id) === Number(channelId));
  const activeMemberIds = Array.isArray(activeChannel?.memberIds) ? activeChannel.memberIds.map(Number) : [];
  const activeMemberNames = users.filter((person) => activeMemberIds.includes(Number(person.id))).map((person) => person.name);

  const openMembers = () => {
    if (!activeChannel) return;
    setSelectedMemberIds(activeMemberIds);
    setMembersOpen(true);
  };

  const toggleMember = (personId) => {
    const normalizedId = Number(personId);
    setSelectedMemberIds((current) => current.includes(normalizedId)
      ? current.filter((id) => id !== normalizedId)
      : [...current, normalizedId]);
  };

  const saveMembers = async () => {
    if (!activeChannel || membersSaving) return;
    setMembersSaving(true);
    try {
      const { data } = await api.put(`/crm/internal-chat/channels/${activeChannel.id}/members`, { memberIds: selectedMemberIds });
      setChannels((current) => current.map((channel) => Number(channel.id) === Number(data.id) ? data : channel));
      setMembersOpen(false);
      toast.success("Usuarios asignados al canal.");
    } catch (error) { toastError(error); }
    finally { setMembersSaving(false); }
  };

  return <MainContainer>
    <Paper elevation={0} className={classes.chatShell}>
      <aside className={classes.channelsPanel} aria-label="Canales internos">
        <div className={classes.channelsHeader}>
          <Typography className={classes.channelsTitle}>Canales</Typography>
          <Tooltip title="Crear canal interno" arrow><IconButton color="primary" aria-label="Crear canal interno" onClick={createChannel}><Add /></IconButton></Tooltip>
        </div>
        <List className={classes.channelsList} disablePadding>
          {channels.map((channel) => <ListItem className={classes.channelItem} button selected={Number(channel.id) === Number(channelId)} onClick={() => loadChat(channel.id)} key={channel.id}>
            <ListItemText primary={`# ${channel.name}`} primaryTypographyProps={{ className: classes.channelText }} />
          </ListItem>)}
        </List>
      </aside>
      <section className={classes.conversation} aria-label="Conversación interna">
        <div className={classes.conversationHeader}>
          <ChatBubbleOutline className={classes.headerIcon} />
          <div className={classes.headerCopy}>
            <Typography className={classes.headerText}># {activeChannel?.name || "Seleccioná un canal"}</Typography>
            {activeChannel && <Typography variant="caption" color="textSecondary" className={classes.headerMembers}>
              {activeMemberNames.length ? activeMemberNames.join(", ") : "Sin usuarios asignados"}
            </Typography>}
          </div>
          <Tooltip title="Asignar usuarios al canal" arrow>
            <span><IconButton color="primary" disabled={!activeChannel} onClick={openMembers} aria-label="Asignar usuarios al canal"><GroupAdd /></IconButton></span>
          </Tooltip>
        </div>
        <div
          className={classes.messages}
          ref={messagesRef}
          onScroll={(event) => {
            const node = event.currentTarget;
            stayAtBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
          }}
        >
          {messages.map((message) => {
            const own = Number(message.senderId) === Number(user?.id);
            return <div key={message.id} className={`${classes.messageRow} ${own ? classes.ownMessage : ""}`}>
              <Paper className={`${classes.messageBubble} ${own ? classes.ownBubble : ""}`}>
                <Typography variant="caption" color="textSecondary" className={classes.messageMeta}>{message.senderName} · {formatMontevideoTime(message.createdAt)}</Typography>
                <Typography variant="body2">{message.body}</Typography>
              </Paper>
            </div>;
          })}
          {!messages.length && <Typography className={classes.empty} color="textSecondary">Todavía no hay mensajes en este canal. Es un espacio privado para la coordinación interna del equipo.</Typography>}
        </div>
        <form onSubmit={sendMessage} className={classes.composer}>
          <TextField className={classes.composerInput} fullWidth multiline maxRows={4} disabled={!channelId || sending} placeholder="Escribí un mensaje interno…" helperText="Ctrl + Enter para enviar" value={body} onChange={(event) => setBody(event.target.value)} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter") sendMessage(event); }} />
          <Tooltip title="Enviar mensaje interno" arrow><span><IconButton className={classes.sendButton} type="submit" color="primary" disabled={!body.trim() || !channelId || sending} aria-label="Enviar mensaje interno"><Send /></IconButton></span></Tooltip>
        </form>
      </section>
    </Paper>
    <Dialog open={membersOpen} onClose={() => !membersSaving && setMembersOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle>Asignar usuarios a #{activeChannel?.name || "canal"}</DialogTitle>
      <DialogContent dividers>
        {users.map((person) => <ListItem button key={person.id} onClick={() => toggleMember(person.id)}>
          <ListItemIcon><Checkbox edge="start" color="primary" checked={selectedMemberIds.includes(Number(person.id))} tabIndex={-1} /></ListItemIcon>
          <ListItemText primary={person.name} secondary={person.email || (person.profile === "admin" ? "Administrador" : "Usuario")} />
        </ListItem>)}
        {!users.length && <Typography color="textSecondary">Todavía no hay usuarios disponibles para asignar.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button disabled={membersSaving} onClick={() => setMembersOpen(false)}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={membersSaving} onClick={saveMembers}>{membersSaving ? "Guardando…" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  </MainContainer>;
};

export default InternalChat;
